// Bridges the mobile DOM controls (joystick / action / cancel) to the game's
// existing keyboard-reading controllers. A singleton — there is one active game.

const THRESHOLD = 0.4;

class VirtualInput {
  constructor() {
    this._vec = { x: 0, y: 0 };
    this._action = null;   // { label, fn }
    this._cancel = null;   // fn
    this._subs = new Set();
    this._stickUsers = new Set();   // controllers that read the joystick (walk, car)
  }

  get vector() { return this._vec; }

  setVector(x, y) { this._vec = { x, y }; }

  clearVector() { this._vec = { x: 0, y: 0 }; }

  isDown(dir) {
    const { x, y } = this._vec;
    switch (dir) {
      case 'up':    return y < -THRESHOLD;
      case 'down':  return y >  THRESHOLD;
      case 'left':  return x < -THRESHOLD;
      case 'right': return x >  THRESHOLD;
      default:      return false;
    }
  }

  _notify() { for (const cb of this._subs) cb(); }

  subscribe(cb) {
    this._subs.add(cb);
    return () => this._subs.delete(cb);
  }

  registerAction(label, fn) { this._action = { label, fn }; this._notify(); }
  clearAction() { if (this._action) { this._action = null; this._notify(); } }
  getAction() { return this._action; }
  invokeAction() { if (this._action) this._action.fn(); }

  // The joystick shows only while something reads it: a shop or a board game has nothing to
  // walk, and a stick there only hides the scene. Owners claim it on create, release on shutdown.
  wantStick(owner) { this._stickUsers.add(owner); this._notify(); }
  releaseStick(owner) {
    if (!this._stickUsers.delete(owner)) return;
    if (!this._stickUsers.size) this.clearVector();
    this._notify();
  }
  stickWanted() { return this._stickUsers.size > 0; }
  /** Claim the stick for a Phaser scene's lifetime (released on its shutdown). */
  wantStickFor(scene, owner) {
    this.wantStick(owner);
    scene.events.once('shutdown', () => this.releaseStick(owner));
    scene.events.once('destroy', () => this.releaseStick(owner));
  }

  registerCancel(fn) { this._cancel = fn; this._notify(); }
  clearCancel() { if (this._cancel) { this._cancel = null; this._notify(); } }
  getCancel() { return this._cancel; }
  invokeCancel() { if (this._cancel) this._cancel(); }
}

export const virtualInput = new VirtualInput();
